// Düzeltilmiş AuthContext.jsx - çeviri fonksiyonu prop olarak alınıyor
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { getFromStorage, saveToStorage, removeFromStorage, clearUserData } from '../utils/storage';
import { STORAGE_KEYS, SECURITY_LEVELS, SCREENS } from '../utils/constants';
import MerkleAuth from '../utils/merkleAuth';

// Auth Context oluştur
const AuthContext = createContext();

/**
 * Authentication Provider - Kimlik doğrulama durumlarını yönetir
 * Optimize edilmiş, sonsuz döngü sorunları giderilmiş, 2FA entegrasyonu eklenmiş
 * Güvenlik geliştirmeleri ve i18n desteği eklendi
 * 
 * Not: Bu bileşen için çeviri fonksiyonu (t) props olarak geçirilmelidir,
 * bu nedenle TranslationWrapper içinde kullanılmalıdır
 */
export const AuthProvider = ({ children, t = str => str, translationLanguage }) => {
  // t prop olarak geldiği için useTranslation hook'unu burada kullanmıyoruz

  // Temel durum değişkenleri
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState({
    id: 'user-123456',
    name: 'Alihan Kalkan',
    username: '@Alihan',
    profilePhoto: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
    bloodType: 'AB+',
    height: '189 cm',
    weight: '78 kg',
    languages: ['Türkçe', 'English'],
    phones: ['+90 123 456 78 90'],
    emails: [
      'aliyandro.channel@gmail.com',
      'akalkan@hozio.com'
    ],
    bio: 'I Have egg Allargy.',
    walletAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F'
  });
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [authState, setAuthState] = useState('idle'); // idle, authenticating, verified, failed

  // İlklendirme ve işlem takibi için ref'ler
  const isInitialized = useRef(false);
  const authRequestInProgress = useRef(false);
  const merkleAuthRef = useRef(null);
  const redirectionInProgress = useRef(false);
  const lastAuthentication = useRef(null);
  const currentProcess = useRef(null);

  // Ekran kategorileri - güvenlik ve navigasyon kontrolü için
  const screenCategories = useRef({
    // Kimlik doğrulama gerektirmeyen ekranlar
    publicScreens: ['welcome', 'login', 'signup', 'forgotPassword'],

    // Oturum ve kayıt işlemi süreçleri için ekranlar - string değerleri kullanır
    registrationScreens: ['signupPhone', 'voiceSignup', 'verifyCode', 'accountInfo', 'interestSelection'],

    // Sadece yetkili kullanıcıların erişebileceği ekranlar
    protectedScreens: ['dashboard', 'profile', 'digitalVault', 'notifications', 'messaging', 'qrScanner', 'identityCircle', 'endlessNotes'],

    // Özel güvenlik yetkilendirmesi gerektiren ekranlar
    secureScreens: ['twoFactorAuth']
  });

  // Demo kullanıcı bilgileri - güvenli bir nesne içinde kapsüllenmiş
  const demoUserData = useRef({
    id: 'user-123456',
    name: 'Alihan Kalkan',
    username: '@Alihan',
    profilePhoto: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
    bloodType: 'AB+',
    height: '189 cm',
    weight: '78 kg',
    languages: ['Türkçe', 'English'],
    phones: ['+90 123 456 78 90'],
    emails: [
      'aliyandro.channel@gmail.com',
      'akalkan@hozio.com'
    ],
    bio: 'I Have egg Allargy.',
    walletAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F'
  });

  // MerkleAuth Başlatma
  useEffect(() => {
    // MerkleAuth nesnesini yalnızca bir kez oluştur
    if (!merkleAuthRef.current) {
      merkleAuthRef.current = new MerkleAuth();

      // 2FA durumunu yerel depolamadan yükle
      const stored2FAState = getFromStorage(STORAGE_KEYS.TWO_FA_ENABLED, false);
      setIs2FAEnabled(stored2FAState);

      // 2FA verilerini yükle (eğer varsa)
      if (stored2FAState) {
        const encryptedData = getFromStorage(STORAGE_KEYS.TWO_FA_DATA);
        if (encryptedData) {
          try {
            // Şifresiz olarak yüklemeyi dene (test amaçlı - gerçek uygulamada şifre gerekir)
            merkleAuthRef.current.importEncryptedData(encryptedData, 'default-secure-password');
          } catch (err) {
            console.warn(t('auth.2fa.loadError', '2FA verileri otomatik olarak yüklenemedi:'), err.message);
          }
        }
      }
    }
  }, [t]);

  // Ekran kategorisini kontrol etme yardımcı fonksiyonu
  const getScreenCategory = useCallback((screenName) => {
    if (!screenName) return null;

    for (const [category, screens] of Object.entries(screenCategories.current)) {
      if (screens.includes(screenName)) {
        return category;
      }
    }
    return null;
  }, []);

  // Ekran erişimine izin verip vermeme kontrolü
  const canAccessScreen = useCallback((screenName) => {
    const category = getScreenCategory(screenName);

    if (!category) return true; // Eğer bir kategori bulunamazsa, varsayılan olarak erişime izin ver

    if (category === 'publicScreens') return true;
    if (category === 'registrationScreens') return true; // Kayıt ekranlarına her zaman erişilebilir

    // Korumalı ve güvenli ekranlar için yetkilendirme kontrolü
    if (category === 'protectedScreens' || category === 'secureScreens') {
      return isAuthenticated;
    }

    return false;
  }, [getScreenCategory, isAuthenticated]);

  // Güvenli ekran erişimi kontrolü
  const validateScreenAccess = useCallback((currentScreen, targetScreen) => {
    // Eğer zaten bir yönlendirme işlemi devam ediyorsa, başka bir istek yapılmamalı
    if (redirectionInProgress.current) {
      console.log(t('auth.navigation.inProgress', 'Yönlendirme işlemi zaten devam ediyor.'));
      return false;
    }

    // Eğer hedef ekran geçerli değilse, erişim engellenir
    if (!targetScreen) {
      console.error(t('auth.navigation.invalidScreen', 'Geçersiz ekran adı.'));
      return false;
    }

    // Hedef ekranın kategorisini kontrol et
    const currentCategory = getScreenCategory(currentScreen);
    const targetCategory = getScreenCategory(targetScreen);

    // Güvenli ekranlara geçiş sırasında ek güvenlik kontrolleri
    if (targetCategory === 'secureScreens') {
      // Eğer son kimlik doğrulama işlemi 5 dakikadan eskiyse, yeniden doğrulama gerekir
      const lastAuthTime = lastAuthentication.current;
      const currentTime = Date.now();

      if (!lastAuthTime || (currentTime - lastAuthTime > 5 * 60 * 1000)) {
        // Yeniden kimlik doğrulama gerekiyor
        console.warn(t('auth.security.reAuthRequired', 'Güvenli ekranlara erişim için yeniden kimlik doğrulama gerekiyor.'));
        return false;
      }
    }

    // Kullanıcı kimlik doğrulaması gerektiren ekranlara erişebilir mi?
    if ((targetCategory === 'protectedScreens' || targetCategory === 'secureScreens') && !isAuthenticated) {
      console.warn(t('auth.security.authRequired', 'Kimlik doğrulaması olmadan korumalı ekranlara erişilemiyor.'));
      return false;
    }

    return true;
  }, [getScreenCategory, isAuthenticated, t]);

  // Uygulama başlangıcında yerel depolamadan oturum durumunu kontrol et
  useEffect(() => {
    // Eğer zaten başlatıldıysa, tekrar çalıştırma
    if (isInitialized.current) return;
    isInitialized.current = true;

    const checkAuth = async () => {
      try {
        setAuthState('authenticating');
        // Yerel depolamadan kullanıcı ve token bilgisini al
        const savedUser = getFromStorage(STORAGE_KEYS.USER);
        const authToken = getFromStorage(STORAGE_KEYS.AUTH_TOKEN);

        // Token kontrolü
        if (savedUser && authToken) {
          // Token'ın geçerlilik süresini kontrol et (tokenData içinde expires bilgisi varsa)
          if (authToken.expires && authToken.expires < Date.now()) {
            // Token süresi dolmuşsa çıkış yap
            clearUserData();
            setAuthState('failed');
          } else {
            // Geçerli oturum
            // TODO: FUTURE_ENABLE - Auto-login is temporarily disabled for testing/UI refinement purposes. 
            // Uncomment the lines below to re-enable session persistence.
            /*
            setUser(savedUser);
            setIsAuthenticated(true);
            lastAuthentication.current = Date.now(); // Son kimlik doğrulama zamanını güncelle
            setAuthState('verified');
            */
            console.log('Auto-login disabled for testing. User details found in storage but ignored.');
          }
        } else {
          // Oturum açılmamış
          setAuthState('idle');
        }
      } catch (error) {
        console.error(t('auth.session.checkError', 'Oturum durumu kontrol edilirken hata oluştu:'), error);
        setAuthError(t('auth.session.loadError', 'Oturum bilgileri yüklenirken bir hata oluştu.'));
        setAuthState('failed');
      } finally {
        // Yükleme durumunu kapat
        setLoading(false);
      }
    };

    // Kimlik doğrulama durumunu kontrol et
    checkAuth();
  }, [t]); // t dependency eklendi

  // Güvenli ekran kontrolü ve yönlendirme
  useEffect(() => {
    // Kimlik doğrulaması yapılmış kullanıcıları giriş ekranlarında tutmamak için
    // VoiceSignup ekranı artık kayıt ekranları kategorisinde, bu yüzden kontrol dışında tutulacak
    const { publicScreens, registrationScreens } = screenCategories.current;

    // AuthContext.jsx içinde handleAuthenticatedNavigation fonksiyonunda debug eklemesi
    const handleAuthenticatedNavigation = async () => {
      // Debug log
      console.log('[DEBUG AUTH] handleAuthenticatedNavigation running. Current screen:', window.currentScreen);

      // BASIT FIX: VoiceSignup ekranını özel durum olarak ele al
      if (window.currentScreen === 'voiceSignup') {
        console.log('[DEBUG AUTH] VoiceSignup ekranında - kimlik doğrulama yönlendirmesi atlanıyor');
        return;
      }

      // Eğer zaten bir yönlendirme devam ediyorsa veya yükleme durumunda ise, işlem yapma
      if (redirectionInProgress.current || loading) return;

      try {
        if (isAuthenticated) {
          // Mevcut ekran kategorilerini kontrol et
          const isOnPublicScreen = publicScreens.includes(window.currentScreen);
          const isRegistrationScreen = registrationScreens.includes(window.currentScreen);

          console.log('[DEBUG AUTH] Ekran kategori kontrolü:', {
            isOnPublicScreen,
            isRegistrationScreen,
            currentScreen: window.currentScreen
          });

          // Dashboard yönlendirme şartları
          if (isOnPublicScreen && !isRegistrationScreen && canAccessScreen(SCREENS.DASHBOARD)) {
            console.log('[DEBUG AUTH] Dashboard yönlendirme şartları sağlandı');

            // Geçişi başlat
            redirectionInProgress.current = true;

            // Doğrudan setTimeout kullanarak yönlendirmeyi ertele
            setTimeout(() => {
              if (window.navigateToScreen) {
                window.navigateToScreen(SCREENS.DASHBOARD);
              }

              // Yönlendirme bayrağını sıfırla
              setTimeout(() => {
                redirectionInProgress.current = false;
              }, 200);
            }, 100);
          } else {
            console.log('[DEBUG AUTH] Otomatik dashboard yönlendirme şartları sağlanmadı');
          }
        }
      } catch (error) {
        console.error('Kimlik doğrulaması sonrası yönlendirme hatası:', error);
        redirectionInProgress.current = false;
      }
    };

    handleAuthenticatedNavigation();
  }, [isAuthenticated, loading, canAccessScreen, t]); // t dependency eklendi

  // Giriş işlemi - useCallback ile optimize edilmiş
  const login = useCallback((username, password) => {
    // Eğer zaten bir istek işleniyorsa, tekrar işleme
    if (authRequestInProgress.current) {
      return Promise.reject(new Error(t('auth.login.inProgress', 'İşlem zaten devam ediyor')));
    }

    // İşlem kayıtları
    currentProcess.current = 'login';

    // İstek başlatma
    authRequestInProgress.current = true;
    setAuthError(null);
    setLoading(true);
    setAuthState('authenticating');

    // Simüle edilmiş bir API çağrısı
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          if (username && password) {
            const authToken = {
              value: 'demo-token-' + Math.random().toString(36).substring(2),
              expires: Date.now() + 24 * 60 * 60 * 1000 // 24 saat geçerli
            };

            // Kullanıcı bilgilerini ve token'ı kaydet
            setUser(demoUserData.current);
            setIsAuthenticated(true);
            lastAuthentication.current = Date.now(); // Son kimlik doğrulama zamanını güncelle
            setAuthState('verified');

            // 2FA durumunu kontrol et
            const stored2FAState = getFromStorage(STORAGE_KEYS.TWO_FA_ENABLED, false);
            setIs2FAEnabled(stored2FAState);

            // Yerel depolamaya kaydet
            saveToStorage(STORAGE_KEYS.USER, demoUserData.current);
            saveToStorage(STORAGE_KEYS.AUTH_TOKEN, authToken);

            // Durum güncellemeleri
            setLoading(false);
            authRequestInProgress.current = false;
            currentProcess.current = null;
            resolve(demoUserData.current);
          } else {
            setLoading(false);
            setAuthError(t('auth.login.credentialsRequired', 'Kullanıcı adı ve şifre gereklidir.'));
            authRequestInProgress.current = false;
            currentProcess.current = null;
            setAuthState('failed');
            reject(new Error(t('auth.login.credentialsRequired', 'Kullanıcı adı ve şifre gereklidir.')));
          }
        } catch (error) {
          setLoading(false);
          setAuthError(t('auth.login.error', 'Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.'));
          authRequestInProgress.current = false;
          currentProcess.current = null;
          setAuthState('failed');
          reject(error);
        }
      }, 800); // Gerçekçi bir gecikme ekle
    });
  }, [t]); // t dependency eklendi

  // Çıkış işlemi - useCallback ile optimize edilmiş
  const logout = useCallback(() => {
    // İşlem kayıtları
    currentProcess.current = 'logout';

    setUser(null);
    setIsAuthenticated(false);
    lastAuthentication.current = null;
    setAuthState('idle');

    // Tüm kullanıcı verilerini temizle
    clearUserData();

    // İşlem tamamlandı
    currentProcess.current = null;

    return Promise.resolve(true);
  }, []);

  // Kayıt işlemi - useCallback ile optimize edilmiş
  const register = useCallback((userData) => {
    // Eğer zaten bir istek işleniyorsa, tekrar işleme
    if (authRequestInProgress.current) {
      return Promise.reject(new Error(t('auth.register.inProgress', 'İşlem zaten devam ediyor')));
    }

    // İşlem kayıtları
    currentProcess.current = 'register';

    authRequestInProgress.current = true;
    setAuthError(null);
    setLoading(true);
    setAuthState('authenticating');

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // Temel doğrulama
          if (userData) {
            // Yeni kullanıcı bilgilerini oluştur
            const newUser = {
              ...demoUserData.current,
              ...userData,
              id: 'user-' + Math.random().toString(36).substring(2)
            };

            // Token bilgisi
            const authToken = {
              value: 'demo-token-' + Math.random().toString(36).substring(2),
              expires: Date.now() + 24 * 60 * 60 * 1000 // 24 saat geçerli
            };

            // Kullanıcı bilgilerini set et
            setUser(newUser);
            setIsAuthenticated(true);
            lastAuthentication.current = Date.now(); // Son kimlik doğrulama zamanını güncelle
            setAuthState('verified');

            // Yerel depolamaya kaydet
            saveToStorage(STORAGE_KEYS.USER, newUser);
            saveToStorage(STORAGE_KEYS.AUTH_TOKEN, authToken);

            // Durum güncellemeleri
            setLoading(false);
            authRequestInProgress.current = false;
            currentProcess.current = null;
            resolve(newUser);
          } else {
            setLoading(false);
            setAuthError(t('auth.register.userDataRequired', 'Geçerli kullanıcı bilgileri gereklidir.'));
            authRequestInProgress.current = false;
            currentProcess.current = null;
            setAuthState('failed');
            reject(new Error(t('auth.register.userDataRequired', 'Geçerli kullanıcı bilgileri gereklidir.')));
          }
        } catch (error) {
          setLoading(false);
          setAuthError(t('auth.register.error', 'Kayıt işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.'));
          authRequestInProgress.current = false;
          currentProcess.current = null;
          setAuthState('failed');
          reject(error);
        }
      }, 1000);
    });
  }, [t]); // t dependency eklendi

  // Şifre sıfırlama - useCallback ile optimize edilmiş
  const resetPassword = useCallback((email) => {
    // Eğer zaten bir istek işleniyorsa, tekrar işleme
    if (authRequestInProgress.current) {
      return Promise.reject(new Error(t('auth.resetPassword.inProgress', 'İşlem zaten devam ediyor')));
    }

    // İşlem kayıtları
    currentProcess.current = 'resetPassword';

    authRequestInProgress.current = true;
    setAuthError(null);
    setLoading(true);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          if (email) {
            // API çağrısı simülasyonu başarılı
            setLoading(false);
            authRequestInProgress.current = false;
            currentProcess.current = null;
            resolve({ success: true, message: t('auth.resetPassword.success', 'Şifre sıfırlama bağlantısı gönderildi.') });
          } else {
            setLoading(false);
            setAuthError(t('auth.resetPassword.emailRequired', 'Geçerli bir e-posta adresi gereklidir.'));
            authRequestInProgress.current = false;
            currentProcess.current = null;
            reject(new Error(t('auth.resetPassword.emailRequired', 'Geçerli bir e-posta adresi gereklidir.')));
          }
        } catch (error) {
          setLoading(false);
          setAuthError(t('auth.resetPassword.error', 'Şifre sıfırlama işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.'));
          authRequestInProgress.current = false;
          currentProcess.current = null;
          reject(error);
        }
      }, 800);
    });
  }, [t]); // t dependency eklendi

  // Polygon wallet bağlama - useCallback ile optimize edilmiş
  const connectWallet = useCallback(async () => {
    // İşlem kayıtları
    currentProcess.current = 'connectWallet';

    setAuthError(null);

    // Wallet bağlantısı simülasyonu - gerçek uygulamada MetaMask veya WalletConnect entegrasyonu olacaktır
    try {
      // Demo wallet adresi
      const walletAddress = '0x71C7656EC7ab88b098defB751B7401B5f6d8976F';

      // Kullanıcı bilgilerini güncelle
      const updatedUser = {
        ...user,
        walletAddress,
        arweaveWallet: 'arweave-wallet-' + Math.random().toString(36).substring(2)
      };

      // State ve local storage güncelleme
      setUser(updatedUser);
      saveToStorage(STORAGE_KEYS.USER, updatedUser);

      // İşlem tamamlandı
      currentProcess.current = null;

      return Promise.resolve({
        address: walletAddress,
        network: 'Polygon',
        chainId: '0x89' // Polygon Mainnet
      });
    } catch (error) {
      currentProcess.current = null;
      setAuthError(t('auth.wallet.connectError', 'Cüzdan bağlama işlemi sırasında bir hata oluştu.'));
      return Promise.reject(error);
    }
  }, [user, t]); // t dependency eklendi

  // Arweave cüzdanı bağlama - Polygon ağına entegre veri depolama için
  const connectArweaveWallet = useCallback(async () => {
    // İşlem kayıtları
    currentProcess.current = 'connectArweaveWallet';

    setAuthError(null);

    try {
      // Demo Arweave cüzdan adresi
      const arweaveAddress = 'arweave-' + Math.random().toString(36).substring(2);

      // Kullanıcı bilgilerini güncelle
      const updatedUser = {
        ...user,
        arweaveWallet: arweaveAddress
      };

      // State ve local storage güncelleme
      setUser(updatedUser);
      saveToStorage(STORAGE_KEYS.USER, updatedUser);

      // İşlem tamamlandı
      currentProcess.current = null;

      return Promise.resolve({
        address: arweaveAddress,
        balance: '0.01 AR'
      });
    } catch (error) {
      currentProcess.current = null;
      setAuthError(t('auth.wallet.arweaveError', 'Arweave cüzdan bağlama işlemi sırasında bir hata oluştu.'));
      return Promise.reject(error);
    }
  }, [user, t]); // t dependency eklendi

  // Kullanıcı profilini güncelleme - useCallback ile optimize edilmiş ve geliştirilmiş
  const updateUserProfile = useCallback(async (profileData) => {
    // İşlem kayıtları ve güvenlik kontrolü
    if (currentProcess.current === 'updateUserProfile') {
      console.warn('Profile update already in progress');
      return user; // Zaten işlem devam ediyorsa mevcut user'ı döndür
    }

    currentProcess.current = 'updateUserProfile';
    setAuthError(null);
    setLoading(true);

    try {
      if (!profileData || !user) {
        throw new Error(t('auth.profile.dataRequired', 'Geçerli profil bilgileri gereklidir.'));
      }

      // Veri temizleme ve doğrulama
      const sanitizedData = {};

      // Güvenli şekilde veri kopyalama
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== undefined && profileData[key] !== null) {
          sanitizedData[key] = profileData[key];
        }
      });

      // Güncellenen kullanıcı verisi oluştur
      const updatedUser = {
        ...user,
        ...sanitizedData,
        lastUpdated: new Date().toISOString(),
        version: (user.version || 0) + 1 // Versiyon takibi
      };

      console.log('📝 AuthContext: Updating user profile:', {
        oldData: { name: user.name, email: user.email },
        newData: { name: updatedUser.name, email: updatedUser.email },
        version: updatedUser.version
      });

      // State güncelleme (derhal)
      setUser(updatedUser);

      // Local storage güncelleme (async)
      await new Promise(resolve => {
        saveToStorage(STORAGE_KEYS.USER, updatedUser);
        resolve();
      });

      // Tüm profile ekranlarına broadcast event
      const profileUpdateEvent = new CustomEvent('userProfileUpdated', {
        detail: {
          updatedUser,
          timestamp: Date.now(),
          source: 'AuthContext.updateUserProfile'
        }
      });

      window.dispatchEvent(profileUpdateEvent);

      // Spesifik profile ekranları için özel event'ler
      ['ProfileScreen', 'AccountInfoScreen', 'ContactSettingsScreen'].forEach(screenName => {
        window.dispatchEvent(new CustomEvent(`${screenName}Refresh`, {
          detail: { updatedUser, timestamp: Date.now() }
        }));
      });

      setLoading(false);
      currentProcess.current = null;

      console.log('✅ AuthContext: Profile updated successfully', updatedUser);
      return updatedUser;

    } catch (error) {
      console.error('❌ AuthContext: Profile update failed:', error);
      setLoading(false);
      setAuthError(t('auth.profile.updateError', 'Profil güncelleme işlemi sırasında bir hata oluştu.'));
      currentProcess.current = null;
      throw error;
    }
  }, [user, t]);

  // Kullanıcı kimlik doğrulama - useCallback ile optimize edilmiş
  const verifyIdentity = useCallback((credentials) => {
    // İşlem kayıtları
    currentProcess.current = 'verifyIdentity';

    setAuthError(null);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // Biyometrik veya şifre doğrulama simülasyonu
          if (credentials) {
            lastAuthentication.current = Date.now(); // Son kimlik doğrulama zamanını güncelle
            currentProcess.current = null;
            resolve({ success: true, verified: true });
          } else {
            setAuthError(t('auth.identity.verificationFailed', 'Kimlik doğrulama başarısız.'));
            currentProcess.current = null;
            reject(new Error(t('auth.identity.verificationFailed', 'Kimlik doğrulama başarısız.')));
          }
        } catch (error) {
          setAuthError(t('auth.identity.error', 'Kimlik doğrulama sırasında bir hata oluştu.'));
          currentProcess.current = null;
          reject(error);
        }
      }, 800);
    });
  }, [t]); // t dependency eklendi

  // Oturum durumunu kontrol et - refresh token mekanizması ile
  const checkSession = useCallback(async () => {
    try {
      // Token bilgisini al
      const authToken = getFromStorage(STORAGE_KEYS.AUTH_TOKEN);

      // Token yoksa veya süresi geçmişse çıkış yap
      if (!authToken || (authToken.expires && authToken.expires < Date.now())) {
        await logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error(t('auth.session.checkError', 'Oturum kontrolünde hata:'), error);
      await logout();
      return false;
    }
  }, [logout, t]); // t dependency eklendi

  // 2FA Kurulumu
  const setup2FA = useCallback(async () => {
    // İşlem kayıtları
    currentProcess.current = 'setup2FA';

    try {
      if (!merkleAuthRef.current) {
        merkleAuthRef.current = new MerkleAuth();
      }

      // Kullanıcı cüzdan adresi gerekir
      if (!user || !user.walletAddress) {
        throw new Error(t('auth.2fa.walletRequired', '2FA kurulumu için cüzdan bağlantısı gereklidir'));
      }

      // 2FA kurulumunu başlat
      const setupData = await merkleAuthRef.current.initializeSetup(user.walletAddress);

      // İşlem tamamlandı
      currentProcess.current = null;

      return setupData;
    } catch (error) {
      currentProcess.current = null;
      console.error(t('auth.2fa.setupError', '2FA kurulum hatası:'), error);
      throw error;
    }
  }, [user, t]); // t dependency eklendi

  // 2FA Doğrulama
  const verify2FACode = useCallback((code) => {
    // İşlem kayıtları
    currentProcess.current = 'verify2FACode';

    try {
      if (!merkleAuthRef.current) {
        throw new Error(t('auth.2fa.notSetup', '2FA henüz kurulmadı'));
      }

      const isValid = merkleAuthRef.current.verifyTOTP(code);

      if (isValid) {
        // 2FA durumunu güncelle
        setIs2FAEnabled(true);
        saveToStorage(STORAGE_KEYS.TWO_FA_ENABLED, true);

        // 2FA verilerini şifreli olarak sakla
        const encryptedData = merkleAuthRef.current.exportEncryptedData('default-secure-password');
        saveToStorage(STORAGE_KEYS.TWO_FA_DATA, encryptedData);

        // Son kimlik doğrulama zamanını güncelle
        lastAuthentication.current = Date.now();
      }

      // İşlem tamamlandı
      currentProcess.current = null;

      return isValid;
    } catch (error) {
      currentProcess.current = null;
      console.error(t('auth.2fa.verifyError', '2FA doğrulama hatası:'), error);
      return false;
    }
  }, [t]); // t dependency eklendi

  // 2FA devre dışı bırakma
  const disable2FA = useCallback((code) => {
    // İşlem kayıtları
    currentProcess.current = 'disable2FA';

    try {
      if (!merkleAuthRef.current) {
        throw new Error(t('auth.2fa.notSetup', '2FA henüz kurulmadı'));
      }

      const isValid = merkleAuthRef.current.verifyTOTP(code);

      if (isValid) {
        // 2FA durumunu güncelle
        setIs2FAEnabled(false);
        removeFromStorage(STORAGE_KEYS.TWO_FA_ENABLED);
        removeFromStorage(STORAGE_KEYS.TWO_FA_DATA);

        // MerkleAuth nesnesini temizle
        merkleAuthRef.current = new MerkleAuth();

        // İşlem tamamlandı
        currentProcess.current = null;

        return true;
      }

      // İşlem tamamlandı
      currentProcess.current = null;

      return false;
    } catch (error) {
      currentProcess.current = null;
      console.error(t('auth.2fa.disableError', '2FA devre dışı bırakma hatası:'), error);
      return false;
    }
  }, [t]); // t dependency eklendi

  // Mevcut 2FA kodunu al
  const getCurrent2FACode = useCallback(() => {
    try {
      if (!merkleAuthRef.current || !is2FAEnabled) {
        return null;
      }

      return merkleAuthRef.current.getCurrentTOTP();
    } catch (error) {
      console.error(t('auth.2fa.getCodeError', '2FA kod alma hatası:'), error);
      return null;
    }
  }, [is2FAEnabled, t]); // t dependency eklendi

  // 2FA verilerini dışa aktar
  const export2FAData = useCallback((password) => {
    // İşlem kayıtları
    currentProcess.current = 'export2FAData';

    try {
      if (!merkleAuthRef.current || !is2FAEnabled) {
        currentProcess.current = null;
        return null;
      }

      const result = merkleAuthRef.current.exportEncryptedData(password);

      // İşlem tamamlandı
      currentProcess.current = null;

      return result;
    } catch (error) {
      currentProcess.current = null;
      console.error(t('auth.2fa.exportError', '2FA veri dışa aktarma hatası:'), error);
      return null;
    }
  }, [is2FAEnabled, t]); // t dependency eklendi

  // 2FA verilerini içe aktar
  const import2FAData = useCallback((encryptedData, password) => {
    // İşlem kayıtları
    currentProcess.current = 'import2FAData';

    try {
      if (!merkleAuthRef.current) {
        merkleAuthRef.current = new MerkleAuth();
      }

      const success = merkleAuthRef.current.importEncryptedData(encryptedData, password);

      if (success) {
        setIs2FAEnabled(true);
        saveToStorage(STORAGE_KEYS.TWO_FA_ENABLED, true);
        saveToStorage(STORAGE_KEYS.TWO_FA_DATA, encryptedData);
      }

      // İşlem tamamlandı
      currentProcess.current = null;

      return success;
    } catch (error) {
      currentProcess.current = null;
      console.error(t('auth.2fa.importError', '2FA veri içe aktarma hatası:'), error);
      return false;
    }
  }, [t]); // t dependency eklendi

  // 2FA doğrulama kanıtı oluştur
  const generate2FAProof = useCallback(() => {
    try {
      if (!merkleAuthRef.current || !is2FAEnabled) {
        return null;
      }

      return merkleAuthRef.current.generateAuthProof();
    } catch (error) {
      console.error(t('auth.2fa.proofError', '2FA kanıt oluşturma hatası:'), error);
      return null;
    }
  }, [is2FAEnabled, t]); // t dependency eklendi

  // Global navigasyon işlevi için window nesnesine ekle - çok dikkatli kullanılmalı!
  // Döngüsel bağımlılıkları önlemek için geçici bir çözüm
  useEffect(() => {
    // Sadece geliştirme ortamında aktif olsun
    if (process.env.NODE_ENV === 'development') {
      // Global navigasyon referansı
      window.currentScreen = 'welcome'; // Başlangıç ekranı

      // Yönlendirme için global bir fonksiyon tanımla
      window.navigateToScreen = (screen) => {
        console.log(`[AUTH] Global navigateToScreen çağrıldı: ${screen}`);
        // Bu fonksiyon, UI Context'teki navigateToScreen fonksiyonu tarafından doldurulacak
      };

      // Temizleme işlemi - uygulama kapatıldığında global referansları temizle
      return () => {
        delete window.navigateToScreen;
        delete window.currentScreen;
      };
    }
  }, [t]);

  // Arweave işlemleri
  const arweaveOperations = {
    /**
     * Arweave'e veri yükleme
     * @param {Object|string} data - Yüklenecek veri
     * @param {Object} tags - Metadata etiketleri
     * @returns {Promise<string>} İşlem ID'si
     */
    uploadToArweave: useCallback(async (data, tags = {}) => {
      // İşlem kayıtları
      currentProcess.current = 'uploadToArweave';

      try {
        if (!user?.walletAddress) {
          throw new Error(t('auth.arweave.walletRequired', 'Arweave işlemleri için wallet bağlantısı gereklidir'));
        }

        const result = await merkleAuthRef.current.uploadToArweave(
          data,
          tags,
          user.walletAddress
        );

        // İşlem tamamlandı
        currentProcess.current = null;

        return result;
      } catch (error) {
        currentProcess.current = null;
        console.error(t('auth.arweave.uploadError', 'Arweave yükleme hatası:'), error);
        throw error;
      }
    }, [user, t]), // t dependency eklendi

    /**
     * Arweave'den veri alma
     * @param {string} txId - İşlem ID'si
     * @returns {Promise<Object|string>} Alınan veri
     */
    downloadFromArweave: useCallback(async (txId) => {
      // İşlem kayıtları
      currentProcess.current = 'downloadFromArweave';

      try {
        if (!user?.walletAddress) {
          throw new Error(t('auth.arweave.walletRequired', 'Arweave işlemleri için wallet bağlantısı gereklidir'));
        }

        const result = await merkleAuthRef.current.downloadFromArweave(
          txId,
          user.walletAddress
        );

        // İşlem tamamlandı
        currentProcess.current = null;

        return result;
      } catch (error) {
        currentProcess.current = null;
        console.error(t('auth.arweave.downloadError', 'Arweave indirme hatası:'), error);
        throw error;
      }
    }, [user, t]), // t dependency eklendi

    /**
     * Kullanıcının Arweave işlemlerini listele
     * @returns {Promise<Array>} İşlem listesi
     */
    listTransactions: useCallback(async () => {
      // İşlem kayıtları
      currentProcess.current = 'listTransactions';

      try {
        if (!user?.walletAddress) {
          throw new Error(t('auth.arweave.walletRequired', 'Arweave işlemleri için wallet bağlantısı gereklidir'));
        }

        const result = await merkleAuthRef.current.listUserTransactions(
          user.walletAddress
        );

        // İşlem tamamlandı
        currentProcess.current = null;

        return result;
      } catch (error) {
        currentProcess.current = null;
        console.error(t('auth.arweave.listError', 'Arweave işlem listesi hatası:'), error);
        throw error;
      }
    }, [user, t]), // t dependency eklendi

    /**
     * Arweave'e not kaydet
     * @param {Object} note - Kaydedilecek not
     * @param {boolean} isPermanent - Kalıcı saklama modu
     * @returns {Promise<string>} İşlem ID'si
     */
    saveNote: useCallback(async (note, isPermanent = false) => {
      // İşlem kayıtları
      currentProcess.current = 'saveNote';

      try {
        if (!user?.walletAddress) {
          throw new Error(t('auth.arweave.walletRequired', 'Arweave işlemleri için wallet bağlantısı gereklidir'));
        }

        const result = await merkleAuthRef.current.saveNoteToArweave(
          note,
          user.walletAddress,
          isPermanent
        );

        // İşlem tamamlandı
        currentProcess.current = null;

        return result;
      } catch (error) {
        currentProcess.current = null;
        console.error(t('auth.notes.saveError', 'Not kaydetme hatası:'), error);
        throw error;
      }
    }, [user, t]), // t dependency eklendi

    /**
     * Arweave'den notları getir
     * @returns {Promise<Array>} Not listesi
     */
    getAllNotes: useCallback(async () => {
      // İşlem kayıtları
      currentProcess.current = 'getAllNotes';

      try {
        if (!user?.walletAddress) {
          throw new Error(t('auth.arweave.walletRequired', 'Arweave işlemleri için wallet bağlantısı gereklidir'));
        }

        const result = await merkleAuthRef.current.getAllNotesFromArweave(
          user.walletAddress
        );

        // İşlem tamamlandı
        currentProcess.current = null;

        return result;
      } catch (error) {
        currentProcess.current = null;
        console.error(t('auth.notes.getError', 'Not alma hatası:'), error);
        throw error;
      }
    }, [user, t]) // t dependency eklendi
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        authError,
        is2FAEnabled,
        authState,
        login,
        logout,
        register,
        resetPassword,
        connectWallet,
        connectArweaveWallet,
        updateUserProfile,
        verifyIdentity,
        checkSession,
        canAccessScreen,
        validateScreenAccess,
        getScreenCategory,
        // 2FA fonksiyonları
        setup2FA,
        verify2FACode,
        disable2FA,
        getCurrent2FACode,
        export2FAData,
        import2FAData,
        generate2FAProof,
        // Arweave işlemleri
        arweaveOperations
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Context için hook
export const useAuth = () => useContext(AuthContext);